import React, {useState, useRef, useEffect, useMemo, Fragment} from "react";
import styled from "styled-components";
import {Icon, Spacer, Text, Theme} from "@dspworkplace/ui";
import Modal from "../Modal";
import Loading, {LoadingWrapper} from "../Loading";
import SimpleScroll from "../SimpleScroll";
import moment from "moment";
import Empty from "../Empty";
import {GetTimeLineEntity} from "../../domain/event/api/TimelineEntity";
import {useIntersection} from "../../hooks/useIntersect";
import {toast} from "../Toasts";
import {portalPromise} from "../Dialog";

const DateContent = styled.span`
    font-size: ${Theme.font.extraSmall.size};
    line-height: ${Theme.font.extraSmall.lineHeight};
    font-family: ${Theme.font.main};
    font-weight: bold;
    color: ${Theme.colors.neutrals.medium};
`;

const HistContent = styled.div`
    > .hasPadding {
        cursor:pointer;

        &:last-child {
            padding-bottom:0;

            .info {
                &:before {
                    min-height:0;
                }
            }

            .hidden {
                &:before {
                    top: -8px;
                    height: calc(100% + 18px);
                }
            }
        }

        .info {
            flex: 1;
            justify-content: flex-start;

            &:before {
                content: ' ';
                border-left: 1px dashed ${Theme.colors.info.border};
                display: block;
                min-height: 30px;
                top: 50%;
                position: absolute;
                margin-left: 5px;
            }
        }

    }
`;

export const FlexScroll = styled.div`
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    overflow-x: hidden;
    height: calc(100vh - 135px);

    //&::-webkit-scrollbar {
    //    width:0;
    //}

    &::-webkit-scrollbar-track-piece {
        background: #EAF0F6;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #7C98B6
    }

    // height: auto;
    width: auto;
`;

const Status = styled.span`
    background: ${Theme.colors.info.border};
    height: 8px;
    width: 8px;
    border-radius: 100%;
    display: inline-block;
    margin-left: 2px;
    margin-right: 2px;
    position: relative;
    z-index: 2;
    box-sizing: border-box;

    &.open {
        background: #fff;
        border: 2px solid ${Theme.colors.info.border};
        width: 12px;
        height: 12px;
        margin-left: 0;
        margin-right: 0;
    }

    &.error {
        background:${Theme.colors.error.border};
        border-color: ${Theme.colors.error.border};
    }
`;

const StatusContainer = styled.div`
    display:flex;
    justify-content:space-between;
    align-items:center;
    position:relative;
    flex-wrap:wrap;
    border-color: ${Theme.colors.info.border};

    &.hasPadding {
        padding-bottom:8px;
    }
`;

const StatusTitle = styled.h5`
    color: ${Theme.colors.info.text};
    font-size: ${props => (props.fontSize ? props.fontSize : Theme.font.medium.size)};
    line-height: ${Theme.font.medium.lineHeight};
    font-family: ${Theme.font.main};
    font-weight: normal;
    display: inline-block;
    position: relative;
    left: 6px;

    &.error {
        color:${Theme.colors.error.text};
    }
`;

const HiddenContent = styled.div`
    flex-basis: 100%;
    margin-left: 20px;
    padding: 8px;
    border-width: 1px;
    border-style: solid;
    border-color: inherit;
    position:relative;
    border-radius:4px;
    margin-top:4px;

    &:before {
        content: ' ';
        border-left: 1px dashed ${Theme.colors.info.border};
        position: absolute;
        left: -16px;
        top: 13px;
        height: calc(100% + 12px);
    }
`;

const ExtraInfos = styled.div`
    > p {
        color: ${Theme.colors.neutrals.medium};
        display:inline-block;
        font-family: ${Theme.font.body};
    }
    
    > p.title {
        color:#707070;
        width:100px;
    }
`;

const Header = styled.div`
    display:flex;
    justify-content:space-between;
`;

const Title = styled.h5`
    color: ${Theme.colors.info.text};
    font-size: ${props => (props.fontSize ? props.fontSize : Theme.font.medium.size)};
    line-height: ${Theme.font.medium.lineHeight};
    font-family: ${Theme.font.main};
    font-weight: normal;
    display: inline-block;
    position: relative;
    left: 6px;
    width : ${props => (props.fontSize ? '406px' : 'inherit')};

    &.error {
        color: ${Theme.colors.error.text};
    }
`;

const solveIcon = (type, color) => {
    let icon = Icon[type] ? Icon[type] : Icon.Info;
    if (type === "SVGBattery") {
        return <>
            <svg fill={Theme.colors.info.border} height="16px" width="16px" version="1.1" id="Layer_1" viewBox="0 0 500 340" xmlns="http://www.w3.org/2000/svg"><defs></defs>
            <path d="M472,222.6c1.2,0,2,0.8,2,2v101.6h2c14.4,0,26-11.6,26-26v-96c0-14.4-12.4-26-26.8-26h-4.4 c-1.2,0-2-0.8-2-2v-6c0-27.6-22.8-49.2-50-49.2H54c-27.6,0-48,19.6-48,46.8v168.4c0,27.6,21.6,49.6,48.8,49.6H420 c27.6,0,50-22.4,50-49.6v-8.4V224.2C470,223.4,470.8,222.6,472,222.6z M438,220.2V333c0,14.4-9.2,28-23.6,28h-356 C44,361,34,347,34,332.6V172.2c0-14.4,10-23.2,24.4-23.2h356c14.4,0,24.8,10.4,24.8,24.4L438,220.2z"></path><g><path d="M250,191c0-3.2-2.8-6-6-6h-16c-3.2,0-6,2.8-6,6v128c0,3.2,2.8,6,6,6h16c3.2,0,6-2.8,6-6V191z"></path><path d="M142,191c0-3.2-2.8-6-6-6h-16c-3.2,0-6,2.8-6,6v128c0,3.2,2.8,6,6,6h16c3.2,0,6-2.8,6-6V191z"></path><path d="M358,191c0-3.2-2.8-6-6-6h-16c-3.2,0-6,2.8-6,6v128c0,3.2,2.8,6,6,6h16c3.2,0,6-2.8,6-6V191z"></path></g><path d="M56.8,392.2C26,392.2,0,366.6,0,336.6V168.2c0-29.6,25.2-52.8,57.6-52.8h364.8c30.8,0,55.6,24.8,55.6,55.2v2 c16.8,0.4,30,14.4,30,32v96c0,17.6-14.4,32-32,32l0,0v4.4c0,30.8-24.8,55.6-55.2,55.6L56.8,392.2z M57.6,123 C29.6,123,8,142.6,8,167.8v168.4c0,26,22.4,47.6,48.8,47.6h364c26,0,47.2-21.6,47.2-47.6v-8.4c0-1.2,0.4-2,1.2-2.8 c0.8-0.8,2-1.2,2.8-1.2h4c13.2,0,24-10.8,24-24v-96c0-13.2-10-24-23.2-24l0,0H474c-2,0-3.6-1.6-4-3.6l-0.4-6 c0-26.4-21.2-47.6-47.6-47.6H57.6V123z"></path><path d="M472,333c-2.4,0-4-1.6-4-4V225c0-2.4,1.6-4,4-4s4,1.6,4,4v104C476,331.4,474.4,333,472,333z"></path><path d="M418,363H62c-15.6,0-30-14.8-30-30.4V172.2c0-16,15.2-25.2,30-25.2h356c15.6,0,27.2,11.6,27.2,26.8c0,2.4-1.6,4-4,4 s-4-1.6-4-4c0-10.8-8-18.8-19.2-18.8H62c-10.8,0-22,6.4-22,17.2v160.4c0,11.2,11.2,22.4,22,22.4h356.4c11.2,0,18-11.2,18-22V221 c0-2.4,1.6-4,4-4s4,1.6,4,4v112C444,347.8,434.4,363,418,363z"></path><path d="M244,331h-16c-7.2,0-12-6.8-12-13.2v-128c0-6.4,4.8-10.8,12-10.8h16c7.2,0,12,4.4,12,10.8v128C256,324.2,251.2,331,244,331z M228,187c-1.6,0-4,0.4-4,2.8v128c0,2.4,2,5.2,4,5.2h16c2,0,4-2.8,4-5.2v-128c0-2.4-2.4-2.8-4-2.8H228z"></path><path d="M136,331h-16c-7.2,0-12-6.8-12-13.2v-128c0-6.4,4.8-10.8,12-10.8h16c7.2,0,12,4.4,12,10.8v128C148,324.2,143.2,331,136,331z M120,187c-1.6,0-4,0.4-4,2.8v128c0,2.4,2,5.2,4,5.2h16c2,0,4-2.8,4-5.2v-128c0-2.4-2.4-2.8-4-2.8H120z"></path><path d="M352,331h-16c-7.2,0-12-6.8-12-13.2v-128c0-6.4,4.8-10.8,12-10.8h16c7.2,0,12,4.4,12,10.8v128C364,324.2,359.2,331,352,331z M336,187c-1.6,0-4,0.4-4,2.8v128c0,2.4,2,5.2,4,5.2h16c2,0,4-2.8,4-5.2v-128c0-2.4-2.4-2.8-4-2.8H336z"></path>
            </svg>
        </>;
    }
    return React.createElement(
        icon,
        {
            size: '16px',
            color: color || Theme.colors.info.text,
            style: {
                verticalAlign: 'middle'
            }
        }
    )
};

const HistComponent = ({data}) => {
    const [open, setOpen] = useState([]);

    const handleHiddenContent = (index) => {
        if (open.includes(index)) {
            const copy = open.filter((item) => item !== index);

            setOpen(copy);
        } else {
            setOpen([...open, index]);
        }
    }

    return (
        <HistContent>
            {data.line && Array.isArray(data.line) && data.line.map((info, i) => (
                <StatusContainer className='hasPadding' onClick={() => {
                    handleHiddenContent(i)
                }} key={i}>
                    <StatusContainer className='info'>
                        <Status
                            className={[info.status === 'incident' ? 'error' : '', open.includes(i) ? 'open' : '']}/>
                        <Spacer left={2}>
                            {solveIcon(info.iconType, info.status === 'incident' ? Theme.colors.error.text : null)}
                            {typeof info.title === "string" && info.title.length > 54 &&
                            <StatusTitle className={[info.status === 'incident' ? 'error' : '']} fontSize={'11px'}>
                                {info.title}
                            </StatusTitle>}
                            {typeof info.title === "string" && info.title.length <= 53 &&
                            <StatusTitle className={[info.status === 'incident' ? 'error' : '']}>
                                {info.title}
                            </StatusTitle>}
                        </Spacer>
                    </StatusContainer>
                    {typeof info.date === "string"?
                        <DateContent>{moment(info.date).local().format('h:mma')}</DateContent>
                        :
                        <DateContent>{moment.utc(info.date).local().format('h:mma')}</DateContent>}
                    {open.includes(i) && <ExtraData info={info}/>}
                </StatusContainer>
            ))}
        </HistContent>
    )
}

const ExtraData = ({info}) => {
    return (
        <HiddenContent className='hidden'>
            <Spacer bottom={1}>
                <Text dangerouslySetInnerHTML={{__html: info.information}}/>
                {info.entries && info.entries.map((e, k) =>
                    <Spacer top={2} key={k}>
                        <ExtraInfos>
                            <Text className='title'>{e.name}</Text>
                            <Text>{e.value}</Text>
                        </ExtraInfos>
                    </Spacer>
                )}
                {['DRIVER_BATTERY_PERCENTAGE','AMAZON_DOES_MEAL_OR_REST'].includes(info.type) && info?.groups &&
                <Spacer top={2}>
                    <RowBatteryPercentageTimeline data={info.groups} date={info.date} />
                </Spacer>}
            </Spacer>
        </HiddenContent>
    )
}

const RowBatteryPercentageTimeline = ({ data, date }) => {
    
    return (
        <HistContent>
            {Array.isArray(data) && data.map((info, i) => (
                <Fragment key={info.id}>
                    <StatusContainer className={[info.information == "N/A" ? "noPointer hasPadding" : " hasPadding"]}>
                        <StatusContainer className='info'>
                            <Status  color={info.groups ? Theme.colors.primary : null }/>
                            <Spacer left={2}>
                                <Title fontSize={'12px'}>
                                    {info.title+'%'} {typeof info.date === "string" ? <DateContent style={{float:'right'}}>{moment(info.date).local().format('h:mma')}</DateContent> : <DateContent style={{float:'right'}}>{moment.utc(info.date).local().format('h:mma')}</DateContent>}
                                </Title>
                            </Spacer>
                        </StatusContainer>
                    </StatusContainer>
                </Fragment>
            ))}
        </HistContent>
    )
};

export const TimelineSmart = ({
   visible = false,
   setVisible = () => {},
   drawer = 'right',
   apiParams = {},
   title
}) => {
    const [status, setStatus] = useState('pending')
    const [data, setData] = useState([])
    const [date, setDate] = useState()

    const LoaderRef = React.useRef(null);
    const entry = useIntersection(LoaderRef,{
        threshold :1
    })
    const isLoadingVisible = !!entry?.isIntersecting

    const timeLineHandler = (params) => {
        GetTimeLineEntity(params).then(resolve => {
            setData(oldData => {
                return [...oldData,...resolve.data.events]
            })
            setDate(oldDate => {
                if(oldDate === resolve.data.nextDate) return null
                return resolve.data.nextDate
            })
        }).catch(err => {
            toast({
                type: "error",
                title: 'Unable to load the timeline',
                content: err.message,
                timeout: 5000,
                useClose: true
            })
        }).finally(() => {
            setStatus('ready')
        })
    }

    useEffect(()=>{
        timeLineHandler(apiParams)
    },[apiParams])

    useEffect(()=>{
        if(!isLoadingVisible) return
        if(!date) return

        timeLineHandler({...apiParams,date:date})
    },[apiParams, date, isLoadingVisible])

    return (
        <Modal
            width='506px'
            visible={visible}
            setVisible={setVisible}
            drawer={drawer}
            removeHeader={true}
        >
            <Header>
                {title}
                <div>
                    <Spacer inline left={4}>
                        <Icon.Times
                            color={Theme.colors.neutrals.silver}
                            size='28px'
                            onClick={()=>setVisible(false)}
                        />
                    </Spacer>
                </div>
            </Header>
            <Spacer top={5}/>
            <div style={{width: '100%', height: '1px', background: '#F5F5F5', display: 'block'}}/>
            <Spacer top={5}/>

            {status === 'pending' && (
                <LoadingWrapper>
                    <Loading style={{height: '80px', marginTop: '20px'}}/>
                </LoadingWrapper>
            )}

            {status === 'ready' && data.length < 1 &&
                <Empty/>
            }

            {status === 'ready' && data.length > 0 &&
                <>
                    {
                        data.map(event =>{
                            return (
                                <div key={event.date}>
                                    <DateContent>{moment(event.date).format('dddd, MMM Do Y')}</DateContent>
                                    <Spacer bottom={2}/>
                                    <HistComponent data={event}/>
                                    <Spacer bottom={5}/>
                                </div>
                            )
                        })
                    }
                </>
            }
            <div ref={LoaderRef}>
                {date && <Loading style={{height:25, display: 'block', marginLeft:'auto', marginRight:'auto',padding: '10px 0'}} />}
            </div>
        </Modal>
    )
}

export const openTimeline = (params) => {
    return portalPromise((success) => {
        const setVisible = () => {
            success()
        }
        return <TimelineSmart
            visible={true}
            setVisible={setVisible}
            apiParams={params}
        />
    });
}

const Timeline = ({
                      visible = false,
                      setVisible = () => {
                      },
                      loading = true,
                      title,
                      actions,
                      /**
                       * Format:
                       * [
                       *     // each object represents a day
                       *     {
                       *         id: int
                       *         date: valid moment value - should be UTC
                       *         line: [
                       *             // each object represents an event of that day
                       *             {
                       *                 date: valid moment value - should be UTC
                       *                 iconType: any Icon element
                       *                 title: str,
                       *                 type: any,
                       *                 // optional - show details when clicked
                       *                 entries: [
                       *                     { name: string-like, value: string-like }
                       *                 ]
                       *                 // optional - title of "entries" when clicked
                       *                 information: str
                       *             }
                       *         ]
                       *     }
                       * ]
                       * */
                      data = [],
                      onLoadMore = () => {
                      }, // todo - when we scroll to the bottom of the list call this
                      incidentTimeline = false
                  }) => {
    const [isMoreData, setIsMoreData] = useState(false);
    const [filtered, setFiltered] = useState([]);
    const [allFiltered, setAllFiltered] = useState([]);
    const scrollEl = useRef(null);
    const loadMoreData = async (data) => {
        let itemCount = 0;
        let listData = [];
        if(data && data.length > 0){
            data = await data.sort(function(a,b){return new Date(b.dateDay) - new Date(a.dateDay);}).filter((item, key) => {
                if(itemCount < 100){
                    listData.push(item);
                    itemCount = itemCount + item.line.length;
                    return false;
                }else{
                    return true;
                }
            });
            await setFiltered([...filtered,...listData]);
            await setAllFiltered(data);
        } else{
            await setAllFiltered([]);
        }

        if(data && data.length > 0) {
            setIsMoreData(true);
        }else{
            setIsMoreData(false);
        }
    };
    const scrollChange = () => {
        if(isMoreData){
            if(scrollEl.current.scrollTop + scrollEl.current.offsetHeight > scrollEl.current.scrollHeight - 5){
                loadMoreData(allFiltered)
            }
        }
    };

    useEffect(() => {
        loadMoreData(data ? data.filter(i => i.line.some(e => e.date !== null)) : []);
    },[data]);

    return (
        <Modal
            width='506px'
            visible={visible}
            setVisible={setVisible}
            drawer='right'
            removeHeader={true}
        >
            {loading && (
                <LoadingWrapper>
                    <Loading style={{height: '80px', marginTop: '20px'}}/>
                </LoadingWrapper>
            )}
            {!loading && (
                <>
                    <Header>
                        {title}
                        <div>
                            {actions}
                            <Spacer inline left={4}>
                                <Icon.Times
                                    color={Theme.colors.neutrals.silver}
                                    size='28px'
                                    onClick={() => setVisible(false)}
                                />
                            </Spacer>
                        </div>
                    </Header>
                    <Spacer top={5}/>
                    <div style={{width: '100%', height: '1px', background: '#F5F5F5', display: 'block'}}/>
                    <Spacer top={5}/>
                    <FlexScroll  as={'li'} onScroll={()=>scrollChange()} ref={scrollEl}>
                        {!filtered.length && <Empty/>}
                        {filtered.map((item, index) => (
                            <div key={index}>
                                {incidentTimeline === true ? <DateContent>{moment(item.date).format('dddd, MMM Do Y')} </DateContent> : <DateContent>{moment.utc(item.date).local().format('dddd, MMM Do Y')} </DateContent>}
                                <Spacer bottom={2}/>
                                <HistComponent data={item}/>
                                <Spacer bottom={5}/>
                            </div>
                        ))}
                        {isMoreData && isMoreData == true && (<center>
                            <Loading style={{height: '80px', marginTop: '20px', left: '43%'}}/>
                        </center>)}
                    </FlexScroll>
                </>
            )}
        </Modal>
    );
};

export default Timeline;


